// src/FeaturedManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';
import FileUpload from './FileUpload'; 

const API_URL = 'https://rys-backend.onrender.com/api';

// --- (แก้ไข 1) ย้าย Modal Form ออกมาข้างนอก (แก้บั๊กภาพเพี้ยน) ---
function FileEditorModal({ fileData, onClose, onSave, getAuthHeaders }) {
  
  const [currentFile, setCurrentFile] = useState(
    // (ถ้า fileData เป็น null = โหมด "สร้าง")
    fileData || { id: null, title: '', image_url: '', pdf_url: '', sort_order: 0 }
  );
  
  const isEditing = !!(fileData && fileData.id);

  // (Handler สำหรับ Input ทั้งหมด)
  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentFile(prev => ({ ...prev, [name]: value }));
  };

  // (Handler เมื่ออัปโหลดภาพสำเร็จ)
  const handleUploadSuccess = (newImageUrl) => {
    setCurrentFile(prev => ({ ...prev, image_url: newImageUrl }));
    toast.success("อัปโหลดภาพปกสำเร็จ!");
  };

  // (Handler เมื่อกดบันทึก)
  const handleSave = () => {
    if (!currentFile.title || !currentFile.image_url || !currentFile.pdf_url) {
      toast.error("กรุณากรอกข้อมูลให้ครบทุกช่อง");
      return;
    }
    onSave(currentFile); // (ส่งข้อมูลกลับไปให้แม่)
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
      <div className="bg-white w-full max-w-2xl p-6 rounded-lg shadow-xl m-4">
        <h2 className="text-2xl font-semibold text-gray-800 mb-5 border-b pb-3">
          {isEditing ? `แก้ไขเอกสาร: ${fileData.title}` : 'เพิ่มเอกสารสำคัญใหม่'}
        </h2>
        
        <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
          
          {/* (1. Title) */}
          <div>
            <label htmlFor="title" className="block text-base font-medium text-gray-700">
              หัวข้อ (Title) *
            </label>
            <input
              type="text"
              id="title"
              name="title" 
              value={currentFile.title}
              onChange={handleChange}
              required
              className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm"
            />
          </div>

          {/* (2. Sort Order) */}
          <div>
            <label htmlFor="sort_order" className="block text-base font-medium text-gray-700">
              ลำดับที่ (Sort Order)
            </label>
            <input
              type="number" 
              id="sort_order"
              name="sort_order" 
              value={currentFile.sort_order}
              onChange={handleChange}
              required
              className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm"
            />
            <p className="text-sm text-gray-500 mt-1">
              (ยิ่งน้อยยิ่งอยู่บนสุด)
            </p>
          </div>
          
          {/* (3. Image Upload) */}
          <div>
            <label className="block text-base font-medium text-gray-700">
              ภาพหน้าปก (Thumbnail Image) *
            </label>
            <FileUpload 
              onUploadSuccess={handleUploadSuccess}
              currentImageUrl={currentFile.image_url}
              getAuthHeaders={getAuthHeaders} // (ส่ง getAuthHeaders ที่ถูกต้อง)
            />
          </div>
          
          {/* (4. PDF URL) */}
          <div>
            <label htmlFor="pdf_url" className="block text-base font-medium text-gray-700">
              ลิงก์ PDF (PDF URL) *
            </label>
            <input
              type="text"
              id="pdf_url"
              name="pdf_url" 
              value={currentFile.pdf_url || ''}
              onChange={handleChange}
              required
              className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm"
            />
          </div>
        </div>
        
        {/* (ปุ่ม) */}
        <div className="flex justify-end space-x-3 pt-6 mt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            ยกเลิก
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {isEditing ? 'บันทึกการแก้ไข' : 'สร้างเอกสาร'}
          </button>
        </div>
      </div>
    </div>
  );
}
// --- (สิ้นสุดการย้าย Modal) ---


// (Component หลักของหน้า)
function FeaturedManagement() {
  const [featuredFiles, setFeaturedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  // (แก้ไข 2) ดึง getAuthHeaders จาก useAuth
  const { user, getAuthHeaders } = useAuth();
  
  // (State สำหรับ Modal)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFile, setEditingFile] = useState(null); 
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);

  // (แก้ไข 3) แก้ไข fetchData ให้ใช้ getAuthHeaders ที่ถูกต้อง
  const fetchFeaturedFiles = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/featured-files`, getAuthHeaders()); 
      setFeaturedFiles(response.data);
    } catch (err) {
      toast.error("ไม่สามารถโหลดข้อมูลไฟล์เด่นได้");
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders]); 

  useEffect(() => {
    fetchFeaturedFiles();
  }, [fetchFeaturedFiles]);

  // (แก้ไข 4) ฟังก์ชัน "บันทึก" (รองรับทั้ง Create/Update)
  const handleSaveFile = async (fileToSave) => {
    const isEditing = !!fileToSave.id;
    
    const payload = {
      title: fileToSave.title,
      image_url: fileToSave.image_url,
      pdf_url: fileToSave.pdf_url,
      sort_order: fileToSave.sort_order 
    };
    
    const method = isEditing ? 'put' : 'post';
    const url = isEditing
      ? `${API_URL}/featured-files/${fileToSave.id}`
      : `${API_URL}/featured-files`;
      
    try {
      const response = await axios({ method, url, data: payload, ...getAuthHeaders() });
      toast.success(response.data.msg);
      fetchFeaturedFiles(); // (โหลดซ้ำ)
      setIsModalOpen(false);
      setEditingFile(null);
    } catch (err) {
      toast.error(err.response?.data?.msg || 'บันทึกไม่สำเร็จ');
    }
  };
  
  // (แก้ไข 5) ฟังก์ชัน "ยืนยัน" การลบ
  const handleConfirmDelete = async () => {
    if (!deletingFile) return;
    try {
      const response = await axios.delete(`${API_URL}/featured-files/${deletingFile.id}`, getAuthHeaders());
      toast.success(response.data.msg);
      fetchFeaturedFiles();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'ลบไม่สำเร็จ');
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingFile(null);
    }
  };
  
  // (ฟังก์ชัน "เปิด" Modal)
  const openCreateModal = () => {
    setEditingFile(null); 
    setIsModalOpen(true);
  };
  
  const openEditModal = (file) => {
    setEditingFile(file); 
    setIsModalOpen(true);
  };

  const openDeleteModal = (file) => {
    setDeletingFile(file);
    setIsDeleteModalOpen(true);
  };

  if (user?.role !== 'admin') {
    navigate('/');
    return null;
  }

  return (
    <>
      {/* (แก้ไข 6) เพิ่ม flex-grow เพื่อดัน Footer ลงล่าง */}
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 flex flex-col flex-grow">
        
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-semibold text-gray-800">
            จัดการเอกสารอ้างอิงหลัก
          </h2>
          <div className="flex space-x-3">
            {/* (ปุ่มย้อนกลับ) */}
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition duration-300"
            >
              &larr; ย้อนกลับ
            </button>
            
            {/* (แก้ไข 7) "ปุ่มเพิ่มเอกสารสำคัญ" (ตาม Workflow ใหม่) */}
            <button
              onClick={openCreateModal}
              disabled={featuredFiles.length >= 3} 
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
            >
              + เพิ่มเอกสารสำคัญ
            </button>
          </div>
        </div>
        
        {isLoading ? (
          <p>Loading...</p>
        ) : (
          <div className="bg-white p-6 rounded-lg shadow-xl">
            {/* (แก้ไข 8) แสดงผลเมื่อ "ตารางว่าง" (ตาม Workflow ใหม่) */}
            {featuredFiles.length === 0 ? (
              <p className="text-center text-gray-500">
                ยังไม่มีเอกสารสำคัญ (จำกัดสูงสุด 3 ไฟล์)
              </p>
            ) : (
              // (แก้ไข 9) แสดงผล "ตาราง" (แก้บั๊กคอลัมน์เพี้ยน)
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase w-20">ลำดับ</th>
                    <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase">รูป</th>
                    <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase">หัวข้อ</th>
                    {/*<th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase">Sort Order</th>*/}
                    <th className="px-6 py-3 text-right text-base font-medium text-gray-500 uppercase">ดำเนินการ</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {featuredFiles.map((file, index) => (
                    <tr key={file.id}>
                      
                      <td className="px-6 py-4 text-lg text-center">{file.sort_order}</td>
                      {/*<td className="px-6 py-4 text-lg">{index + 1}</td>*/}
                      <td className="px-6 py-4">
                        <img src={file.image_url} alt={file.title} className="w-24 h-16 object-cover rounded-md" />
                      </td>
                      <td className="px-6 py-4 text-lg font-medium">{file.title}</td>
                      <td className="px-6 py-4 text-right space-x-3">
                        <button onClick={() => openEditModal(file)} className="text-blue-600 hover:text-blue-900">แก้ไข</button>
                        <button onClick={() => openDeleteModal(file)} className="text-red-600 hover:text-red-900">ลบ</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      
      {/* (Modal สำหรับ สร้าง/แก้ไข) */}
      {isModalOpen && (
        <FileEditorModal
          fileData={editingFile}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveFile}
          getAuthHeaders={getAuthHeaders}
        />
      )}
      
      {/* (Modal สำหรับ ลบ) */}
      {isDeleteModalOpen && deletingFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-xl m-4">
            <h2 className="text-2xl font-semibold mb-5 text-gray-800 border-b pb-3">ยืนยันการลบ</h2>
            <p className="mb-6 text-lg">
              คุณแน่ใจหรือไม่ว่าต้องการลบ: <strong className="text-red-600">{deletingFile.title}</strong>?
            </p>
            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                ยืนยันการลบ
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default FeaturedManagement;